/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { PaymentCurrencies } from '@mta/vela-api-types'
import { useAtomValue, useSetAtom } from 'jotai'
import { ImageIcon, Minus, Plus, Trash2 } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import React from 'react'
import { useTrackRemoveFromCart } from '@/hooks/analytics/useTrackRemoveFromCart'
import { Link } from '@/i18n/navigation'
import {
  BasketItemWithProductInformation,
  ItemIdentifierPayload,
  itemPriceAtomFamily,
  removeItemAtom,
  UpdateQuantityPayload,
  updateItemQuantityAtom,
} from '@/store/basket'
import { cn } from '@/utils/css'
import { convertLocaleToLanguage } from '@/utils/locale-to-language'
import { formatPrice } from '@/utils/price-formatting'

/** A description of the available properties for this component. */
type BasketLineItemProps = {
  /** The line item to render. */
  item: BasketItemWithProductInformation

  /** Whether the basket item can be edited (i.e. quantity changed, item removed). */
  allowEditing: boolean

  /** The design variant to render. */
  variant?: 'full' | 'compact'
}

/**
 * A line item in the basket overview, providing the user with information about their basket and enabling them to
 * update it by changing quantities or removing items when possible.
 */
const BasketLineItem: React.FC<BasketLineItemProps> = ({ item, allowEditing, variant = 'full' }) => {
  const t = useTranslations('BasketLineItem')
  const removeItem = useSetAtom(removeItemAtom)
  const updateQuantity = useSetAtom(updateItemQuantityAtom)
  const priceInfo = useAtomValue(itemPriceAtomFamily({ productId: item.productId, variantId: item.variantId }))
  const trackRemoveFromCart = useTrackRemoveFromCart()

  /**
   * Handle an item being removed from the basket.
   *
   * @param payload - the item to remove
   */
  const handleRemoveItem = (payload: ItemIdentifierPayload) => {
    if (!allowEditing) return

    trackRemoveFromCart(item, priceInfo)
    removeItem(payload)
  }

  /**
   * Handle the user changing the quantity of the item in their basket.
   *
   * @param change - the numerical change (i.e. 1, or -1)
   */
  const handleQuantityChange = (change: number) => {
    if (!allowEditing) return

    const newQuantity = item.quantity + change
    if (newQuantity < 1) {
      trackRemoveFromCart(item, priceInfo)
      removeItem({ productId: item.productId, variantId: item.variantId })
    } else {
      const payload: UpdateQuantityPayload = {
        productId: item.productId,
        variantId: item.variantId,
        quantity: newQuantity,
      }

      updateQuantity(payload)
    }
  }

  const displayName = item.details?.name ?? t('productId', { productId: item.productId })
  const displayThumb = item.details?.thumbnail?.source
  const linkHref = `${item.productType === 'kit' ? '/kits/' : '#'}`
  const displayAlt = item.details?.thumbnail?.alt ?? t('thumbnailAlt', { displayName })

  return (
    <li
      className={cn(
        'flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4',
        variant === 'full' && 'p-4',
        variant === 'compact' && 'p-3',
      )}
    >
      <Link
        className={cn(
          'relative shrink-0 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden',
          variant === 'full' && 'w-20 h-20',
          variant === 'compact' && 'w-14 h-14',
        )}
        href={linkHref}
        aria-label={t('viewDetails', { displayName })}
      >
        {displayThumb ? (
          <Image src={displayThumb} alt={displayAlt} className={'object-cover'} fill />
        ) : (
          <ImageIcon className='w-10 h-10 text-gray-400' />
        )}
      </Link>
      <Link className='grow text-center md:text-left' href={linkHref}>
        <h2 className='text-lg font-medium text-primary-500'>{displayName}</h2>
        {item.variantId && (
          <p className='text-sm text-gray-500'>
            {t('language')}: {convertLocaleToLanguage(item.variantId)}
          </p>
        )}
      </Link>

      {allowEditing ? (
        <div
          className={cn(
            'flex items-center shrink-0',
            variant === 'full' && 'space-x-2',
            variant === 'compact' && 'space-x-0.5',
          )}
        >
          <button
            type={'button'}
            onClick={() => handleQuantityChange(-1)}
            className='p-1 rounded-full border hover:bg-gray-100 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2'
            aria-label={t('decreaseQuantity', { displayName })}
            disabled={item.quantity <= 1}
          >
            <Minus aria-hidden={'true'} className='w-4 h-4' />
          </button>
          <span className='w-8 text-center font-medium' aria-live='polite' aria-atomic='true'>
            {item.quantity}
          </span>
          <button
            type={'button'}
            onClick={() => handleQuantityChange(1)}
            className='p-1 rounded-full border hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2'
            aria-label={t('increaseQuantity', { displayName })}
          >
            <Plus aria-hidden={'true'} className='w-4 h-4' />
          </button>
        </div>
      ) : (
        <div className='flex items-center space-x-2 shrink-0 px-4'>
          <span className='text-sm text-gray-500'>{t('quantity')}:</span>
          <span className='font-medium'>{item.quantity}</span>
        </div>
      )}

      <div className='text-center md:text-right font-semibold shrink-0'>
        {priceInfo ? formatPrice(priceInfo.lineTotal, PaymentCurrencies[priceInfo.currency]) : '...'}
      </div>

      {allowEditing && (
        <div className='shrink-0'>
          <button
            type={'button'}
            onClick={() => handleRemoveItem({ productId: item.productId, variantId: item.variantId })}
            className='p-2 rounded-full text-gray-500 hover:text-red-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2'
            aria-label={t('removeProduct', { displayName })}
          >
            <Trash2 aria-hidden={'true'} className='w-5 h-5' />
          </button>
        </div>
      )}
    </li>
  )
}

export { BasketLineItem }
